import { useDispatch } from 'react-redux'
import cn from 'clsx'

// Componetns
import {
  Button,
  Link,
  IconAmexCard,
  IconDinersclubCard,
  IconDiscoverCard,
  IconMaestroCard,
  IconMastercard,
  IconVisaCard,
} from '@tracklib/kit'

// Actions
import { actions as subscriptionActions } from 'modules/subscription/actions'

// Hooks
import { useProfile } from 'modules/profile/hooks'
import { useRequest } from 'modules/requests/hooks'
import { useSavedPaymentMethod } from 'modules/pay/hooks'
import { useSubscription } from 'modules/subscription/hooks'

// Types
import { Subscription } from 'types'

// Utils
import { priceFormatWithVAT } from 'components/base/price'
import formatDate from 'lib/formatdate'

import styles from './currentplan.css'

export const renderCreditCardIcon = (typeName: string): JSX.Element => {
  switch (typeName) {
    case 'mc':
      return <IconMastercard />
    case 'visa':
      return <IconVisaCard />
    case 'amex':
      return <IconAmexCard />
    case 'maestro':
      return <IconMaestroCard />
    case 'diners':
      return <IconDinersclubCard />
    case 'discover':
      return <IconDiscoverCard />
    case 'card':
    default:
      return <img style={{ height: 22 }} src={`https://checkoutshopper-test.adyen.com/checkoutshopper/img/pm/${typeName}@2x.png`} alt="" />
  }
}

export const niceName = (name: string): string => {
  switch (name) {
    case 'bcmc':
      return 'Bancontact'
    case 'mc':
      return 'Mastercard'
    case 'visa':
      return 'Visa'
    case 'amex':
      return 'American Express'
    case 'maestro':
      return 'Maestro'
    case 'diners':
      return 'Diners club'
    case 'discover':
      return 'Discover'
    default:
      return name
  }
}

const renderPayFreq = (months?: number): string => {
  if (months === 1) {
    return 'monthly'
  }
  return 'annual'
}

const statusString = (subscription: Subscription, nextDate: string): string => {
  if (subscription?.state === 'CANCELLED') {
    return `Your plan has been cancelled and will expire ${nextDate}`
  }
  if (subscription?.state === 'PENDING_RETRY') {
    return 'Payment failed! The payment method on file for your Tracklib account has failed to process the latest transaction. Update it below to continue your subscription.'
  }
  const baseMessage =
    'Select the plan that’s best for you and update your payment information. You can cancel your subscription at any time.'
  if (['FREE', 'TERMINATED'].includes(subscription?.state)) {
    return `${baseMessage} You're on the limited plan, which is free, so your card will never be charged.`
  }
  if (subscription?.tier?.is_perpetual) {
    return 'If you have any questions about the VIP plan, contact vip@tracklib.com.'
  }

  if (
    subscription?.next_renewal_period?.tier?.id !== subscription?.tier?.id ||
    subscription?.renewal_period?.months !== subscription?.next_renewal_period?.months
  ) {
    return `Your plan has been downgraded from ${renderPayFreq(subscription.renewal_period?.months)} ${
      subscription.tier?.label
    } to ${renderPayFreq(subscription.next_renewal_period?.months)} ${
      subscription?.next_renewal_period?.tier?.label
    }, the change will take effect at the end of the current billing period, which is ${nextDate}`
  }
  return baseMessage
}

const CurrentPlan = () => {
  const dispatch = useDispatch()
  const profile = useProfile()
  const subscription = useSubscription()
  const tier = subscription?.tier
  const { isLoading } = useRequest('RESUBSCRIBE')
  const savedPaymentMethod = useSavedPaymentMethod()
  const isPerpetual = tier?.is_perpetual
  const nextDate = formatDate(subscription?.next_renewal_date)
  const activeSubscription = ['ACTIVE', 'TRIAL', 'CANCELLED'].includes(subscription?.state)
  const pendingRetry = subscription?.state === 'PENDING_RETRY'
  const hasNextBilling = ['ACTIVE', 'TRIAL'].includes(subscription?.state)

  const discountString = subscription?.discount
    ? `You are on a ${subscription.discount.percentage}% discounted plan until ${formatDate(subscription.discount.end_date)}. `
    : ''
  return (
    <>
      <h2>Plan &amp; Payment</h2>
      <p className={styles.description} datatesting="subscription_status_string">{` ${statusString(subscription, nextDate)}`}</p>
      {subscription ? (
        <>
          <div className={styles.box}>
            <header>
              <div className={styles.plan}>
                <h3 className={styles.tierTitle} datatesting="tier_label">
                  {(activeSubscription && tier?.label) || 'Free plan'}
                  {subscription?.state === 'TRIAL' && ' (Free trial)'}
                  {subscription?.state === 'CANCELLED' && ' (Cancelled)'}
                  {pendingRetry && ' (Suspended)'}
                </h3>
                {tier && activeSubscription && !isPerpetual && (
                  <>
                    <p>
                      <strong datatesting="credits_per_month">{tier.credits_give_per_month} Credits/month</strong>
                      <span
                        className={styles.billed}
                        datatesting="billing_period"
                      >{`Billed ${subscription.renewal_period?.label.toLowerCase()}`}</span>
                    </p>
                    {hasNextBilling && (
                      <p
                        datatesting="next_billing"
                        className={styles.nextBilling}
                      >{`${discountString}Your next bill is for ${priceFormatWithVAT(
                        profile,
                        subscription.renewal_price,
                        subscription.renewal_price_inc_vat,
                      )} on ${nextDate}`}</p>
                    )}
                  </>
                )}
                {(!tier || !activeSubscription) && <p>Our limited and free plan is for listening and license management only.</p>}
              </div>
              {!isPerpetual && (
                <>
                  {activeSubscription && (
                    <Button variant="outline" className={styles.button} to="changeplan" modal>
                      Change plan
                    </Button>
                  )}
                  {!activeSubscription && (
                    <Button variant="outline" className={styles.button} to="/subscribe/">
                      Subscribe
                    </Button>
                  )}
                </>
              )}
            </header>
            <div className={styles.paymentMethodWrapper}>
              {savedPaymentMethod ? (
                <div>
                  <label className={styles.label}>Payment method</label>
                  <div className={styles.paymentMethod}>
                    {renderCreditCardIcon(savedPaymentMethod.variant)}
                    <p datatesting="saved_payment_method">
                      {savedPaymentMethod.variant === 'paypal'
                        ? `PayPal (${savedPaymentMethod.customer_name})`
                        : `${niceName(savedPaymentMethod.variant)} ending in ${savedPaymentMethod.card_number}`}
                    </p>
                  </div>
                </div>
              ) : (
                <p className={styles.noPaymentMethod}>No payment method added</p>
              )}
              <div>
                <Button
                  modal
                  to="paymentmethod"
                  variant="transparent"
                  className={cn(styles.paymentMethodButton, pendingRetry && styles.suspendedButton)}
                  datatesting="button_update_payment_method"
                >
                  {savedPaymentMethod ? 'Update payment details' : 'Add payment method'}
                </Button>
                {pendingRetry && (
                  <Button
                    className={styles.paymentMethodButton}
                    variant="transparent"
                    disabled={isLoading}
                    onClick={() => dispatch(subscriptionActions.resubscribe())}
                  >
                    Resubscribe now
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p>No subscription</p>
      )}
      <footer className={styles.footer}>
        <Link to="receipts" modal className={styles.link}>
          View billing and your receipts
        </Link>
        {(activeSubscription || pendingRetry) && !isPerpetual && (
          <Link to="cancelsubscription" modal className={styles.cancelLink} datatesting="button_open_cancel_subscription_modal">
            Cancel your subscription
          </Link>
        )}
      </footer>
    </>
  )
}

export default CurrentPlan
